import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layers, Share2, Shuffle, StickyNote } from "lucide-react";
import { articlesAPI, tagsAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import { copyTextToClipboard } from "../utils/clipboard";
import Spinner from "../components/Spinner";
import NavigationButtons from "../components/NavigationButtons";
import ScrollToTopButton from "../components/ScrollToTopButton";
import ApprovedTagChips from "../components/ApprovedTagChips";
import { formatReadingEstimate } from "../utils/readingTime";
import {
  getRememberedTutorialLessonId,
  getPublicFlashcardsPath,
  isTutorialLesson,
  getPublicLessonPath,
  getPublicNotesPath,
  rememberTutorialLessonId,
  selectTutorialLesson,
  sortTutorialLessons,
} from "../constants/tutorialContent";
import "./Home.css";
import "./TutorialLesson.css";

const getLessonTags = (lesson) =>
  Array.isArray(lesson?.tags) ? lesson.tags : [];

const TutorialLesson = () => {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const { addToast } = useToasts();
  const [article, setArticle] = useState(null);
  const [tutorialLessons, setTutorialLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareLink, setShareLink] = useState("");
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [sharingLink, setSharingLink] = useState(false);
  const [approvedTags, setApprovedTags] = useState([]);

  const resolvedLessonId = useMemo(() => lessonId || "", [lessonId]);
  const articleTagChips = useMemo(() => {
    const lessonTags = getLessonTags(article);
    if (!lessonTags.length || !approvedTags.length) {
      return [];
    }

    return approvedTags.filter((tag) => lessonTags.includes(tag.slug));
  }, [approvedTags, article]);
  const readingEstimateLabel = formatReadingEstimate(article);

  useEffect(() => {
    let isMounted = true;

    const setResolvedArticle = (nextArticle) => {
      if (!isMounted) return;
      setArticle(nextArticle);
    };

    const loadLesson = async () => {
      setLoading(true);

      try {
        if (resolvedLessonId) {
          const response =
            await articlesAPI.getPublicArticleById(resolvedLessonId);
          const allLessonsResponse = await articlesAPI.getPublicArticles();
          const allLessons = Array.isArray(allLessonsResponse.data)
            ? allLessonsResponse.data
            : [];
          const tutorialPublicLessons = sortTutorialLessons(
            allLessons.filter((lesson) => isTutorialLesson(lesson)),
          );
          if (isMounted) {
            setTutorialLessons(tutorialPublicLessons);
          }
          rememberTutorialLessonId(response.data.article?._id);
          setResolvedArticle(response.data.article);

          const canonicalPath = response.data.article?.publicPathId
            ? getPublicLessonPath(response.data.article)
            : "";
          if (canonicalPath && canonicalPath !== window.location.pathname) {
            navigate(canonicalPath, { replace: true });
          }
          return;
        }

        const response = await articlesAPI.getPublicArticles();
        const lessons = Array.isArray(response.data) ? response.data : [];
        const tutorialPublicLessons = sortTutorialLessons(
          lessons.filter((lesson) => isTutorialLesson(lesson)),
        );
        const rememberedLessonId = getRememberedTutorialLessonId();
        const featuredLesson =
          tutorialPublicLessons.find(
            (lesson) => lesson?._id === rememberedLessonId,
          ) || selectTutorialLesson(lessons);

        if (isMounted) {
          setTutorialLessons(tutorialPublicLessons);
        }
        setResolvedArticle(featuredLesson);
        rememberTutorialLessonId(featuredLesson?._id);
      } catch {
        if (!isMounted) return;
        setArticle(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadLesson();

    return () => {
      isMounted = false;
    };
  }, [lessonId, navigate, resolvedLessonId]);

  useEffect(() => {
    let isMounted = true;

    const loadApprovedTags = async () => {
      try {
        const response = await tagsAPI.getApprovedTags("lesson");
        if (isMounted) {
          setApprovedTags(response.data?.tags || []);
        }
      } catch {
        if (isMounted) {
          setApprovedTags([]);
        }
      }
    };

    loadApprovedTags();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadShareLink = async () => {
      setShareLink("");

      if (!article?._id) {
        setShareLinkLoading(false);
        return;
      }

      setShareLinkLoading(true);

      try {
        const response = await articlesAPI.generateShareLink(article._id);
        const nextShareLink = response?.data?.shareLink || "";

        if (!nextShareLink) {
          throw new Error("Missing share link");
        }

        if (isMounted) {
          setShareLink(nextShareLink);
        }
      } catch {
        if (isMounted) {
          setShareLink("");
        }
      } finally {
        if (isMounted) {
          setShareLinkLoading(false);
        }
      }
    };

    loadShareLink();

    return () => {
      isMounted = false;
    };
  }, [article?._id]);

  const handleShare = async () => {
    if (!article?._id || sharingLink || shareLinkLoading) return;

    if (!shareLink) {
      addToast("Failed to generate share link", "error-warning");
      return;
    }

    setSharingLink(true);
    try {
      await copyTextToClipboard(shareLink);
      addToast("Share link copied to clipboard", "success-info");
    } catch {
      addToast("Failed to copy share link", "error-warning");
    } finally {
      setSharingLink(false);
    }
  };

  const handleViewNotes = () => {
    if (!article?._id || !article.hasNotes) return;
    navigate(getPublicNotesPath(article));
  };

  const handleViewFlashcards = () => {
    if (!article?._id || !article.hasFlashcards) {
      addToast("No flashcards available", "error-warning");
      return;
    }
    navigate(getPublicFlashcardsPath(article));
  };

  const handleRandomLesson = () => {
    if (!tutorialLessons.length) return;

    const alternativeLessons = tutorialLessons.filter(
      (lesson) => lesson?._id !== article?._id,
    );
    const candidatePool = alternativeLessons.length
      ? alternativeLessons
      : tutorialLessons;
    const randomIndex = Math.floor(Math.random() * candidatePool.length);
    const randomLesson = candidatePool[randomIndex];

    if (!randomLesson?._id) return;

    rememberTutorialLessonId(randomLesson._id);
    navigate(getPublicLessonPath(randomLesson));
  };

  const showInitialSpinner = loading && !article;
  const showInlineLoading = loading && !!article;

  if (showInitialSpinner) {
    return <Spinner visible={true} />;
  }

  return (
    <div className="home-layout tutorial-lesson-home-layout">
      {article ? (
        <>
          <NavigationButtons guestRedirectPath="/signup" />
          <div className="home-fixed-buttons tutorial-lesson-fixed-buttons">
            <button
              className={`home-action-button ${tutorialLessons.length ? "" : "is-unavailable"}`.trim()}
              onClick={handleRandomLesson}
              disabled={!tutorialLessons.length || loading}
              title={
                tutorialLessons.length
                  ? "Random"
                  : "No tutorial lessons available"
              }
            >
              <Shuffle size={18} />
            </button>
            <button
              onClick={handleViewNotes}
              className={`home-action-button home-notes-action-button ${article.hasNotes ? "" : "is-unavailable"}`.trim()}
              disabled={!article.hasNotes || loading}
              title={article.hasNotes ? "Notes" : "No notes for this lesson"}
            >
              <StickyNote size={18} />
            </button>
            <button
              onClick={handleViewFlashcards}
              className={`home-action-button home-flashcards-action-button ${article.hasFlashcards ? "" : "is-unavailable"}`.trim()}
              disabled={!article.hasFlashcards || loading}
              title={
                article.hasFlashcards
                  ? "Flashcards"
                  : "No flashcards for this lesson"
              }
            >
              <Layers size={18} />
            </button>
            <button
              className="home-action-button"
              onClick={handleShare}
              disabled={sharingLink || shareLinkLoading || loading}
              title="Share"
            >
              <Share2 size={18} />
            </button>
            <button
              className="home-action-button"
              onClick={() => navigate("/tutorial")}
              title="Tutorial"
            >
              ?
            </button>
          </div>
        </>
      ) : null}

      <div className="home-content-area tutorial-lesson-content-area">
        {article ? (
          <div
            className={`home-lesson-container tutorial-lesson-container ${
              showInlineLoading ? "tutorial-lesson-container-loading" : ""
            }`.trim()}
          >
            {showInlineLoading && (
              <div className="tutorial-lesson-loading-overlay">
                <Spinner visible={true} relative={true} />
              </div>
            )}
            {(article.chapter || article.author) && (
              <div className="home-lesson-title">
                <div className="tutorial-lesson-meta-row">
                  <span className="home-community-pill">From: Tutorial</span>
                  {articleTagChips.length > 0 && (
                    <ApprovedTagChips
                      tags={articleTagChips}
                      size="small"
                      className="tutorial-lesson-display-tags"
                      interactive={false}
                    />
                  )}
                  {readingEstimateLabel && (
                    <span className="tutorial-lesson-reading-estimate">
                      {readingEstimateLabel}
                    </span>
                  )}
                </div>
                {article.chapter && <span>{article.chapter}</span>}
                {article.author && (
                  <span className="home-lesson-author">{article.author}</span>
                )}
              </div>
            )}
            <div className="home-lesson-body">{article.body}</div>
          </div>
        ) : (
          <div className="tutorial-lesson-empty-state">
            <p>No public lessons available</p>
          </div>
        )}
      </div>

      <ScrollToTopButton scrollContainerSelector=".app-app" />
    </div>
  );
};

export default TutorialLesson;
